import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { RichTextEditor } from "@/components/RichTextEditor";
import { htmlToText } from "@/components/RichText";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Loader2, Megaphone, PartyPopper, AlertTriangle, Image as ImageIcon } from "lucide-react";
import { BannerView } from "@/components/StudentBanners";

const EMPTY = { title: "", message: "", style: "info", image_file_id: null, link_url: "", link_label: "", audience: "all", starts_at: "", ends_at: "", dismissible: true, active: true };
const STYLE_ICON = { info: <Megaphone className="w-4 h-4 text-sky-500" />, celebration: <PartyPopper className="w-4 h-4 text-amber-500" />, alert: <AlertTriangle className="w-4 h-4 text-red-500" /> };

export const BannersTab = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [banners, setBanners] = useState([]);
  const [classes, setClasses] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);

  const load = async () => {
    try {
      const [b, c] = await Promise.all([
        axios.get(`${API}/admin/banners`, { headers }),
        axios.get(`${API}/admin/classes`, { headers })
      ]);
      setBanners(b.data || []);
      setClasses(c.data || []);
    } catch (e) { toast.error("Couldn't load banners"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [headers]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setPreviewImg(null); setDialog(true); };
  const openEdit = (b) => {
    setEditing(b.banner_id);
    setForm({ ...EMPTY, ...b, starts_at: b.starts_at ? b.starts_at.slice(0, 16) : "", ends_at: b.ends_at ? b.ends_at.slice(0, 16) : "" });
    setPreviewImg(null);
    if (b.image_file_id) loadPreviewImg(b.image_file_id);
    setDialog(true);
  };

  const loadPreviewImg = async (fileId) => {
    try {
      const r = await axios.get(`${API}/files/${fileId}/download`, { headers, responseType: "blob" });
      setPreviewImg(URL.createObjectURL(r.data));
    } catch (e) { /* preview optional */ }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload-file`, fd, { headers });
      setForm(prev => ({ ...prev, image_file_id: res.data.file_id }));
      setPreviewImg(URL.createObjectURL(file));
      toast.success("Image attached");
    } catch (e) { toast.error("Image upload failed"); }
    finally { setUploadingImg(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null
      };
      delete payload.banner_id; delete payload.created_at; delete payload.created_by;
      if (editing) {
        await axios.put(`${API}/admin/banners/${editing}`, payload, { headers });
        toast.success("Banner updated");
      } else {
        await axios.post(`${API}/admin/banners`, payload, { headers });
        toast.success("Banner published");
      }
      setDialog(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save banner"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (b) => {
    try {
      const payload = { ...b, active: !b.active };
      delete payload.banner_id; delete payload.created_at; delete payload.created_by;
      await axios.put(`${API}/admin/banners/${b.banner_id}`, payload, { headers });
      load();
    } catch (e) { toast.error("Couldn't update"); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/banners/${id}`, { headers });
      toast.success("Banner deleted");
      load();
    } catch (e) { toast.error("Couldn't delete"); }
  };

  return (
    <div className="space-y-4" data-testid="banners-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Banners appear at the top of the Student Dashboard. Schedule them, target a class, and they go live instantly.</p>
        <Button className="rounded-full" onClick={openNew} data-testid="new-banner-btn"><Plus className="w-4 h-4 mr-1.5" />New Banner</Button>
      </div>
      {banners.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-sm text-muted-foreground">No banners yet — create your first announcement banner.</CardContent></Card>
      ) : banners.map(b => (
        <Card key={b.banner_id} className={`border-border/50 ${b.active ? "" : "opacity-60"}`} data-testid={`banner-row-${b.banner_id}`}>
          <CardContent className="py-3 flex items-center gap-3">
            {STYLE_ICON[b.style] || STYLE_ICON.info}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{b.title}</div>
              <div className="text-xs text-muted-foreground truncate">
                {htmlToText(b.message) || "—"} · {b.audience === "all" ? "All students" : (classes.find(c => c.class_id === b.audience)?.name || "One class")}
                {b.ends_at ? ` · until ${new Date(b.ends_at).toLocaleDateString()}` : ""}
              </div>
            </div>
            <Switch checked={b.active} onCheckedChange={() => toggleActive(b)} data-testid={`banner-active-${b.banner_id}`} />
            <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`banner-edit-${b.banner_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(b.banner_id)} data-testid={`banner-delete-${b.banner_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </CardContent>
        </Card>
      ))}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="banner-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Banner" : "New Banner"}</DialogTitle>
            <DialogDescription>Shown at the top of the Student Dashboard.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="banner-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <RichTextEditor value={form.message} onChange={(html) => setForm((f) => ({ ...f, message: html }))} minHeight={70} testId="banner-message-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Style</Label>
                <Select value={form.style} onValueChange={v => setForm({ ...form, style: v })}>
                  <SelectTrigger data-testid="banner-style-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info (blue)</SelectItem>
                    <SelectItem value="celebration">Celebration (gold)</SelectItem>
                    <SelectItem value="alert">Alert (red)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Audience</Label>
                <Select value={form.audience} onValueChange={v => setForm({ ...form, audience: v })}>
                  <SelectTrigger data-testid="banner-audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All students</SelectItem>
                    {classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Starts (optional)</Label>
                <Input type="datetime-local" value={form.starts_at} onChange={e => setForm({ ...form, starts_at: e.target.value })} data-testid="banner-starts-input" />
              </div>
              <div className="space-y-2">
                <Label>Ends (optional)</Label>
                <Input type="datetime-local" value={form.ends_at} onChange={e => setForm({ ...form, ends_at: e.target.value })} data-testid="banner-ends-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Button link (optional)</Label>
                <Input placeholder="https://..." value={form.link_url} onChange={e => setForm({ ...form, link_url: e.target.value })} data-testid="banner-link-input" />
              </div>
              <div className="space-y-2">
                <Label>Button label</Label>
                <Input placeholder="Learn more" value={form.link_label} onChange={e => setForm({ ...form, link_label: e.target.value })} data-testid="banner-link-label-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Image (optional)</Label>
              <div className="flex items-center gap-2">
                <Input type="file" accept="image/*" onChange={e => uploadImage(e.target.files?.[0])} className="flex-1" data-testid="banner-image-input" />
                {uploadingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : form.image_file_id ? <ImageIcon className="w-4 h-4 text-green-600" /> : null}
                {form.image_file_id && <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setForm({ ...form, image_file_id: null }); setPreviewImg(null); }}>Remove</Button>}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Live preview — exactly as students will see it</Label>
              <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl p-3" data-testid="banner-preview">
                <BannerView banner={form} imageUrl={previewImg} testId="banner-preview-view" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch checked={form.dismissible} onCheckedChange={v => setForm({ ...form, dismissible: v })} data-testid="banner-dismissible-switch" />
                <span className="text-sm">Students can dismiss</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} data-testid="banner-active-switch" />
                <span className="text-sm">Active</span>
              </div>
            </div>
            <Button className="w-full" onClick={save} disabled={saving} data-testid="banner-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{editing ? "Save Changes" : "Publish Banner"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
